import React, { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type {
  MarketingRouterExperimentDecision,
  MarketingRouterMetricResult,
  MarketingRouterSignificance,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { type MarketingRouterRuleRecord, useMarketingRouterRules } from '~/hooks/apis/promo/useMarketingRouterRules';
import { useMarketingRouterSignificance } from '~/hooks/apis/promo/useMarketingRouterSignificance';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterInternalQuery } from '~/services/PromoService';

import {
  METRIC_DISPLAY,
  type MarketingRouterMetricKey,
  formatConfidence,
  formatDuration,
  formatMetricTotal,
  formatMetricValue,
  formatRelativeLift,
  formatSessions,
  formatTrafficAllocation,
  getDecisionDisplay,
  getLiftMarginOfErrorPercent,
  getRuleRunStatus,
  summariseVariantSessions,
  totalSessions,
} from './marketingRouterSignificanceDisplay';

type RouteParams = {
  ruleId: string;
};

function formatDate(date: string): string {
  return dayjs(date).format('DD MMM YYYY');
}

function HeaderTile({ label, value, caption }: { label: string; value: React.ReactNode; caption?: string }) {
  return (
    <Grid size={{ xs: 6, md: 2.4 }}>
      <Stack spacing={0.5}>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" component="div">
          {value}
        </Typography>
        {caption ? (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        ) : null}
      </Stack>
    </Grid>
  );
}

function DecisionCard({
  decision,
  baseVariantName,
}: {
  decision: MarketingRouterExperimentDecision;
  baseVariantName: string;
}) {
  const display = getDecisionDisplay(decision, baseVariantName);
  return (
    <Alert severity={display.severity} variant="outlined" sx={{ alignItems: 'flex-start' }}>
      <AlertTitle>{display.title}</AlertTitle>
      {display.description}
    </Alert>
  );
}

function significanceCell(result: MarketingRouterMetricResult) {
  if (result.verdict === 'COLLECTING') {
    return <Chip label="Collecting" color="info" size="small" />;
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
      <Tooltip title={`p-value: ${result.pAdjusted.toPrecision(2)}`}>
        <Typography variant="body2" component="span">
          {formatConfidence(result)}
        </Typography>
      </Tooltip>
      {result.verdict === 'SIGNIFICANT' ? (
        <Chip label="Significant" color={result.absoluteLift >= 0 ? 'success' : 'error'} size="small" />
      ) : null}
    </Stack>
  );
}

function liftCell(result: MarketingRouterMetricResult) {
  const marginOfError = getLiftMarginOfErrorPercent(result.relativeCi95);
  return (
    <Stack spacing={0}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600 }}
        color={result.absoluteLift > 0 ? 'success.main' : result.absoluteLift < 0 ? 'error.main' : 'text.primary'}
      >
        {formatRelativeLift(result.relativeLift)}
      </Typography>
      {marginOfError !== null ? (
        <Typography variant="caption" color="text.secondary">
          give or take {marginOfError.toFixed(1)}pp
        </Typography>
      ) : null}
    </Stack>
  );
}

function MetricSection({
  metric,
  results,
  baseVariantName,
  defaultExpanded,
}: {
  metric: MarketingRouterMetricKey;
  results: MarketingRouterMetricResult[];
  baseVariantName: string;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const display = METRIC_DISPLAY[metric];
  const tier = results[0]?.tier;
  const base = results[0];

  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setExpanded(!expanded)}
          sx={{ cursor: 'pointer' }}
          aria-expanded={expanded}
          role="button"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{display.name}</Typography>
            {tier === 'primary' ? <Chip label="Primary metric" color="primary" size="small" /> : null}
          </Stack>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Stack>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Table size="small" aria-label={`${display.name} results`} sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Variant</TableCell>
                <TableCell align="right">Sessions</TableCell>
                <TableCell align="right">{display.totalLabel}</TableCell>
                <TableCell align="right">{display.perSessionLabel}</TableCell>
                <TableCell align="right">Lift vs control</TableCell>
                <TableCell align="right">Confidence</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {base ? (
                <TableRow>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{baseVariantName}</Typography>
                      <Chip label="Control" size="small" variant="outlined" />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatSessions(base.baseN)}</TableCell>
                  <TableCell align="right">{formatMetricTotal(metric, base.baseTotal)}</TableCell>
                  <TableCell align="right">{formatMetricValue(metric, base.baseValue)}</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">—</TableCell>
                </TableRow>
              ) : null}
              {results.map((result) => (
                <TableRow key={result.variantId}>
                  <TableCell>
                    <Typography variant="body2">{result.variantName}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatSessions(result.treatN)}</TableCell>
                  <TableCell align="right">{formatMetricTotal(metric, result.treatTotal)}</TableCell>
                  <TableCell align="right">{formatMetricValue(metric, result.treatValue)}</TableCell>
                  <TableCell align="right">{liftCell(result)}</TableCell>
                  <TableCell align="right">{significanceCell(result)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function HealthTile({ health }: { health: MarketingRouterSignificance['health'] }) {
  return (
    <HeaderTile
      label="Health"
      value={
        <Tooltip title={`Sample ratio mismatch check, chi-square p = ${health.srm.pValue.toPrecision(2)}`}>
          <Chip
            label={health.srm.healthy ? 'Healthy' : 'Traffic split broken'}
            color={health.srm.healthy ? 'success' : 'error'}
            size="small"
          />
        </Tooltip>
      }
      caption={
        health.overlapRate !== undefined
          ? `${(health.overlapRate * 100).toFixed(1)}% of users also in another experiment`
          : undefined
      }
    />
  );
}

function HealthWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) {
    return null;
  }
  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}
    </Stack>
  );
}

function SummaryTable({
  significance,
  primaryMetric,
}: {
  significance: MarketingRouterSignificance;
  primaryMetric: MarketingRouterMetricKey;
}) {
  const primaryResults = significance.results.filter((result) => result.metric === primaryMetric);
  const base = primaryResults[0];
  const display = METRIC_DISPLAY[primaryMetric];

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Summary
        </Typography>
        <Table size="small" aria-label="Variant summary">
          <TableHead>
            <TableRow>
              <TableCell>Variant</TableCell>
              <TableCell align="right">Sessions</TableCell>
              <TableCell align="right">{display.perSessionLabel}</TableCell>
              <TableCell align="right">Lift vs control</TableCell>
              <TableCell align="right">Confidence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {base ? (
              <TableRow>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{significance.baseVariantName}</Typography>
                    <Chip label="Control" size="small" variant="outlined" />
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatSessions(base.baseN)}</TableCell>
                <TableCell align="right">{formatMetricValue(primaryMetric, base.baseValue)}</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
              </TableRow>
            ) : null}
            {primaryResults.map((result) => (
              <TableRow key={result.variantId}>
                <TableCell>
                  <Typography variant="body2">{result.variantName}</Typography>
                </TableCell>
                <TableCell align="right">{formatSessions(result.treatN)}</TableCell>
                <TableCell align="right">{formatMetricValue(primaryMetric, result.treatValue)}</TableCell>
                <TableCell align="right">{liftCell(result)}</TableCell>
                <TableCell align="right">{significanceCell(result)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RuleDetailContent({ rule }: { rule: MarketingRouterRuleRecord }) {
  const significanceQuery = useMarketingRouterSignificance({
    marketingRouterId: rule.marketingRouterId,
    query: { ruleId: rule.id },
  });
  const significance = significanceQuery.data;

  const now = dayjs();
  const status = getRuleRunStatus(rule.start_date, rule.end_date, now);
  const allocation = formatTrafficAllocation(rule.variants.map((variant) => variant.weight));
  const sessionSummaries = useMemo(
    () =>
      significance
        ? summariseVariantSessions(significance.results, significance.baseVariantId, significance.baseVariantName)
        : [],
    [significance],
  );

  const metricsInOrder = useMemo(() => {
    if (!significance) {
      return [];
    }
    const seen = new Set<MarketingRouterMetricKey>();
    const ordered: MarketingRouterMetricKey[] = [];
    for (const result of significance.results) {
      if (!seen.has(result.metric)) {
        seen.add(result.metric);
        ordered.push(result.metric);
      }
    }
    return ordered;
  }, [significance]);

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <HeaderTile label="Status" value={<Chip label={status.label} color={status.color} size="small" />} />
            <HeaderTile
              label="Sessions"
              value={significance ? formatSessions(totalSessions(sessionSummaries)) : '…'}
              caption={
                sessionSummaries.length > 0
                  ? sessionSummaries
                      .map((summary) => `${summary.variantName}: ${formatSessions(summary.sessions)}`)
                      .join(' · ')
                  : undefined
              }
            />
            <HeaderTile
              label="Duration"
              value={formatDuration(rule.start_date, rule.end_date, now)}
              caption={`${formatDate(rule.start_date)} – ${formatDate(rule.end_date)}`}
            />
            <HeaderTile label="Traffic allocation" value={allocation} caption={`${rule.variants.length} variants`} />
            {significance ? <HealthTile health={significance.health} /> : null}
          </Grid>
          {significance ? <HealthWarnings warnings={significance.health.warnings} /> : null}
        </CardContent>
      </Card>

      {significanceQuery.isLoading ? <Typography color="text.secondary">Loading experiment results…</Typography> : null}
      {significanceQuery.isError ? (
        <Alert severity="error">
          {significanceQuery.error instanceof Error
            ? significanceQuery.error.message
            : 'Failed to load experiment results.'}
        </Alert>
      ) : null}

      {significance ? (
        <>
          <Stack spacing={1}>
            <Typography variant="h6">Decision</Typography>
            {significance.decisions.map((decision) => (
              <DecisionCard
                key={decision.variantId}
                decision={decision}
                baseVariantName={significance.baseVariantName}
              />
            ))}
            {significance.decisions.length === 0 ? (
              <Alert severity="info">This rule has no treatment variants to compare against control.</Alert>
            ) : null}
          </Stack>

          {significance.decisions.length > 0 ? (
            <SummaryTable significance={significance} primaryMetric={significance.decisions[0].primaryMetric} />
          ) : null}

          {metricsInOrder.length > 0 ? (
            <Stack spacing={2}>
              <Typography variant="h6">Metrics</Typography>
              {metricsInOrder.map((metric, index) => (
                <MetricSection
                  key={metric}
                  metric={metric}
                  results={significance.results.filter((result) => result.metric === metric)}
                  baseVariantName={significance.baseVariantName}
                  defaultExpanded={index === 0}
                />
              ))}
            </Stack>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}

export default function MarketingRouterRuleDetailPage() {
  const { ruleId } = useParams<RouteParams>();
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rule = useMemo(
    () => (rulesQuery.data ?? []).find((candidate) => candidate.id === ruleId),
    [rulesQuery.data, ruleId],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Router Rules / {rule?.name ?? 'Rule detail'}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={rule?.name ?? 'Marketing router rule'}>
          <Stack direction="row" spacing={1}>
            <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
              Back to rules
            </Button>
            {rule ? (
              <Button
                component={Link}
                to={`/marketing/marketing-router-rules/${rule.id}/edit?marketingRouterId=${encodeURIComponent(
                  rule.marketingRouterId,
                )}`}
                variant="outlined"
              >
                Edit rule
              </Button>
            ) : null}
          </Stack>
        </PageHeader>
        {rulesQuery.isLoading ? <Typography color="text.secondary">Loading rule…</Typography> : null}
        {rulesQuery.isError ? (
          <Alert severity="error">
            {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rules.'}
          </Alert>
        ) : null}
        {!rulesQuery.isLoading && !rulesQuery.isError && !rule ? (
          <Alert severity="warning">Rule “{ruleId}” was not found for the current tenant.</Alert>
        ) : null}
        {rule ? <RuleDetailContent rule={rule} /> : null}
      </Container>
    </>
  );
}
